import { useState } from "react";
import { useI18n } from "../../i18n";
import { runMockSkill } from "../../lib/mock";
import type { Skill } from "../../types";

const RESULTS_KEY = "deskflow.skillResults";

function loadResults(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(RESULTS_KEY) ?? "{}") as Record<
      string,
      string
    >;
  } catch {
    return {};
  }
}

export function Skills({ skills }: { skills: Skill[] }): React.JSX.Element {
  const { language, t } = useI18n();
  const [selectedId, setSelectedId] = useState(skills[0]?.id ?? "");
  const [input, setInput] = useState("");
  const [savedResults, setSavedResults] = useState(loadResults);
  const [output, setOutput] = useState(
    () => savedResults[skills[0]?.id ?? ""] ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const selected = skills.find((skill) => skill.id === selectedId) ?? skills[0];

  const execute = async (): Promise<void> => {
    if (!selected || !input.trim()) return;
    setBusy(true);
    setError("");
    setOutput("");
    try {
      await new Promise((resolve) => setTimeout(resolve, 450));
      const result = runMockSkill(selected.id, input.trim(), language);
      const next = { ...savedResults, [selected.id]: result };
      setOutput(result);
      setSavedResults(next);
      localStorage.setItem(RESULTS_KEY, JSON.stringify(next));
    } catch {
      setError(t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="skills-grid">
      <article className="panel skill-browser">
        <div className="panel-heading">
          <div>
            <small>{t("skills.starter")}</small>
            <h2>{t("nav.skills")}</h2>
          </div>
          <span className="mock-badge">{t("skills.mockBadge")}</span>
        </div>
        <div className="skill-list">
          {skills.map((skill) => (
            <button
              type="button"
              className={selected?.id === skill.id ? "active" : ""}
              key={skill.id}
              onClick={() => {
                setSelectedId(skill.id);
                setOutput(savedResults[skill.id] ?? "");
                setError("");
              }}
            >
              <span>✦</span>
              <div>
                <strong>
                  {language === "zh-CN" ? skill.nameZh : skill.name}
                </strong>
                <small>
                  {language === "zh-CN"
                    ? skill.descriptionZh
                    : skill.description}
                </small>
              </div>
            </button>
          ))}
        </div>
      </article>
      <article className="panel skill-runner">
        {selected ? (
          <>
            <small>
              {language === "zh-CN" ? selected.nameZh : selected.name}
            </small>
            <h2>
              {language === "zh-CN"
                ? selected.descriptionZh
                : selected.description}
            </h2>
            <label className="field">
              <span>{t("skills.input")}</span>
              <textarea
                rows={10}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder={t("skills.inputPlaceholder")}
              />
            </label>
            <button
              className="text-button example-button"
              type="button"
              onClick={() =>
                setInput(
                  language === "zh-CN"
                    ? "本周完成了产品原型和用户访谈，下周计划优化新手引导并验证任务管理流程。"
                    : "This week we completed the product prototype and user interviews. Next week we will improve onboarding and validate the task workflow.",
                )
              }
            >
              {t("skills.useExample")}
            </button>
            <div className="form-actions">
              <button
                className="primary"
                type="button"
                disabled={busy || !input.trim()}
                onClick={() => void execute()}
              >
                {busy ? t("skills.running") : t("skills.run")}
              </button>
            </div>
            <p className="helper">{t("skills.localNotice")}</p>
            {error && <p className="notice error">{error}</p>}
            {output && (
              <section className="skill-output">
                <small>{t("skills.output")}</small>
                <pre>{output}</pre>
              </section>
            )}
          </>
        ) : (
          <div className="empty-state">
            <p>{t("skills.choose")}</p>
          </div>
        )}
      </article>
    </section>
  );
}
